import * as React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CssBaseline from '@mui/material/CssBaseline';
import Popover from '@mui/material/Popover';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AppTheme from '../shared-theme/AppTheme';
import { useAuth } from '../context/AuthContext';

// Password requirement checker
const checkPasswordRequirements = (password) => ({
  minLength: password.length >= 10,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
});

const allRequirementsMet = (requirements) =>
  Object.values(requirements).every(Boolean);

export default function SignupPage(props) {
  const navigate = useNavigate();
  const { signup, isAuthenticated, error: authError } = useAuth();

  const [displayName, setDisplayName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [passwordAnchorEl, setPasswordAnchorEl] = React.useState(null);
  const [passwordFocused, setPasswordFocused] = React.useState(false);

  // Password requirements state
  const passwordRequirements = checkPasswordRequirements(password);
  const isPasswordValid = allRequirementsMet(passwordRequirements);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate display name
    const trimmedName = displayName.trim();
    if (trimmedName.length < 2 || trimmedName.length > 50) {
      setError('Display name must be between 2 and 50 characters');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    if (!isPasswordValid) {
      setError('Password does not meet all requirements');
      return;
    }

    setLoading(true);

    const result = await signup(email, password, displayName);

    if (result.success) {
      navigate('/app', { replace: true });
    } else {
      setError(result.error || 'Failed to create account');
    }

    setLoading(false);
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Stack spacing={3}>
            {/* Logo/Brand */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src="/icon.svg"
                alt=""
                sx={{
                  width: 60,
                  height: 60,
                  objectFit: 'contain',
                  mb: 1,
                }}
              />
              <Box
                component="img"
                src="/logo.svg"
                alt="myGrow Collective"
                sx={{
                  width: 200,
                  height: 'auto',
                  objectFit: 'contain',
                  mb: 1,
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Create your account
              </Typography>
            </Box>

            {/* Error Alert */}
            {(error || authError) && (
              <Alert severity="error">{error || authError}</Alert>
            )}

            {/* Signup Form */}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Display Name *
                  </Typography>
                  <TextField
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                    placeholder="Enter your name"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Email *
                  </Typography>
                  <TextField
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    autoComplete="email"
                    placeholder="Enter your email"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Password *
                  </Typography>
                  <TextField
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={(e) => {
                      setPasswordAnchorEl(e.currentTarget);
                      setPasswordFocused(true);
                    }}
                    onBlur={() => {
                      setPasswordFocused(false);
                    }}
                    fullWidth
                    required
                    autoComplete="new-password"
                    placeholder="Enter your password"
                    error={password.length > 0 && !isPasswordValid}
                    color={isPasswordValid ? 'success' : undefined}
                  />
                  {/* Password Requirements Popover */}
                  <Popover
                    open={passwordFocused && password.length > 0}
                    anchorEl={passwordAnchorEl}
                    onClose={() => setPasswordFocused(false)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPopover-paper': {
                        mt: 0.5,
                        p: 1.5,
                        borderRadius: 2,
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                      Password Requirements
                    </Typography>
                    <Stack spacing={0.5}>
                      {[
                        { key: 'minLength', label: 'At least 10 characters' },
                        { key: 'hasUppercase', label: 'One uppercase letter (A-Z)' },
                        { key: 'hasLowercase', label: 'One lowercase letter (a-z)' },
                        { key: 'hasNumber', label: 'One number (0-9)' },
                        { key: 'hasSpecial', label: 'One special character (!@#$%...)' },
                      ].map(({ key, label }) => (
                        <Stack key={key} direction="row" alignItems="center" spacing={0.75}>
                          {passwordRequirements[key] ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              color: passwordRequirements[key] ? 'success.main' : 'text.secondary',
                              fontWeight: passwordRequirements[key] ? 500 : 400,
                            }}
                          >
                            {label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Popover>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                    Confirm Password *
                  </Typography>
                  <TextField
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </Stack>
            </form>

            <Divider>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                or
              </Typography>
            </Divider>

            {/* Login Link */}
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" underline="hover">
                Sign in
              </Link>
            </Typography>

            {/* Demo Link */}
            <Button
              component={RouterLink}
              to="/demo"
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Try Demo Mode
            </Button>
          </Stack>
        </Card>
      </Box>
    </AppTheme>
  );
}
